'use client';

/**
 * Pure presentational message-list region for `ChatPanel`.
 *
 * All state, refs, and callbacks live in the parent — this component
 * only decides which branch of the "loading / error / empty / list"
 * conditional to render. Extracted so the verified-holder JSX in
 * `chat-panel.tsx` reads as a flat structure (scroller, message list,
 * typing indicator, jump pill, input) instead of nesting a 40-line
 * ternary chain.
 */

import { Loader2 } from 'lucide-react';
import { ChatMessage, type ChatMessageData } from './chat-message';

export interface ChatMessageListProps {
  allMessages: ChatMessageData[];
  isLoadingMessages: boolean;
  /** Discriminates "fetch in progress, no data yet" from "fetch
   *  finished, genuinely empty". The parent passes the raw query
   *  `data` object through so this component stays stateless; when
   *  `data` is null AND no error, we're still mid-fetch (retry window
   *  between failed attempts). */
  hasData: boolean;
  messagesError: Error | null;
  isFetchingNextPage: boolean;
  /** Current viewer address (lower-cased or checksum — compared to the
   *  stored-lowercase `msg.address`, so the parent normalises). */
  ownAddressLower: string | null;
  canModerate: boolean;
  onEdit: (id: number, message: string) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
  onReact: (id: number, emoji: string) => Promise<void>;
  onReply: (msg: ChatMessageData) => void;
  onScrollToMessage: (id: number) => void;
  /** Ref forwarded to the inner content wrapper. Parent uses it for
   *  its ResizeObserver + initial-scroll-snap effects. */
  contentRef: React.RefObject<HTMLDivElement | null>;
  /** Ref to the trailing anchor; parent scrolls this into view on new
   *  messages when the viewer is pinned at the bottom. */
  bottomRef: React.RefObject<HTMLDivElement | null>;
}

// React's old ref type rejects `RefObject<T | null>` from the standard
// `useRef<T>(null)` call. Type the intrinsic div refs through an
// intermediate cast so the parent's `useRef<HTMLDivElement>(null)`
// passes cleanly. The runtime ref behaviour is unchanged — this is
// purely about JSX prop type variance.
type NullableDivRef = React.Ref<HTMLDivElement>;

export function ChatMessageList({
  allMessages,
  isLoadingMessages,
  hasData,
  messagesError,
  isFetchingNextPage,
  ownAddressLower,
  canModerate,
  onEdit,
  onDelete,
  onReact,
  onReply,
  onScrollToMessage,
  contentRef,
  bottomRef,
}: ChatMessageListProps) {
  // Either the first fetch is in flight OR we're in the retry window
  // between attempts (no cached data + no final error yet). Show the
  // loader in both cases so a transient 429 during the wallet-switch
  // refetch storm doesn't briefly fall through to the empty-state
  // copy below.
  if (isLoadingMessages || (!hasData && !messagesError)) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Query exhausted retries with no cached data. Honest copy beats
  // "No messages yet. Be the first to chat!" — that lie was the
  // headline symptom users saw after signing in with a switched
  // wallet under load.
  if (messagesError && allMessages.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
        Couldn&apos;t load messages. Retrying…
      </div>
    );
  }

  if (allMessages.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-muted-foreground">
        No messages yet. Be the first to chat!
      </div>
    );
  }

  return (
    <div ref={contentRef as NullableDivRef} className="py-2 space-y-0.5">
      {isFetchingNextPage && (
        <div className="flex justify-center py-2">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      )}

      {allMessages.map((msg) => (
        <ChatMessage
          key={msg.id}
          msg={msg}
          isOwn={msg.address === ownAddressLower}
          canModerate={canModerate}
          onEdit={onEdit}
          onDelete={onDelete}
          onReact={onReact}
          onReply={onReply}
          onScrollToMessage={onScrollToMessage}
        />
      ))}
      <div ref={bottomRef as NullableDivRef} />
    </div>
  );
}
